import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import {
  mediumDesktopExactWidth,
  extraDesktopWidthMax,
  mainBgColor,
  mobileWidthLarge,
  smallDesktopWidth,
} from '@edulastic/colors'

const StyledMainContentWrapper = styled.div`
  overflow: auto;
  background: ${mainBgColor};
  padding: ${(props) => props.padding || '30px'};
  width: ${(props) => props.width || '100%'};
  height: ${(props) => {
    const headerHeight =
      props?.mainHeaderHeight || props.theme?.HeaderHeight?.xs
    const totalHeight = props.theme?.isProxyUser
      ? props.theme?.HeaderHeight?.xs +
        props.theme?.BannerHeight +
        (props.isInModal ? 50 : 0)
      : headerHeight + (props.isInModal ? 50 : 0)

    return `calc(100vh - ${totalHeight}px)`
  }};

  @media (min-width: ${mediumDesktopExactWidth}) {
    height: ${(props) => {
      const headerHeight =
        props?.mainHeaderHeight || props.theme?.HeaderHeight?.md
      const totalHeight = props.theme?.isProxyUser
        ? props.theme?.HeaderHeight?.md +
          props.theme?.BannerHeight +
          (props.isInModal ? 50 : 0)
        : headerHeight + (props.isInModal ? 50 : 0)

      return `calc(100vh - ${totalHeight}px)`
    }};
  }
  @media (min-width: ${extraDesktopWidthMax}) {
    height: ${(props) => {
      const headerHeight =
        props?.mainHeaderHeight || props.theme?.HeaderHeight?.xl
      const totalHeight = props.theme?.isProxyUser
        ? props.theme?.HeaderHeight?.xl +
          props.theme?.BannerHeight +
          (props.isInModal ? 50 : 0)
        : headerHeight + (props.isInModal ? 50 : 0)

      return `calc(100vh - ${totalHeight}px)`
    }};
  }
  @media (max-width: ${smallDesktopWidth}) {
    height: ${(props) => {
      const headerHeight =
        props?.mainHeaderHeight || props.theme?.HeaderHeight?.sd
      const totalHeight = props.theme?.isProxyUser
        ? props.theme?.HeaderHeight?.sd +
          props.theme?.BannerHeight +
          (props.isInModal ? 50 : 0)
        : headerHeight + (props.isInModal ? 50 : 0)

      return `calc(100vh - ${totalHeight}px)`
    }};
  }
  @media (max-width: ${mobileWidthLarge}) {
    padding: 20px;
    height: ${(props) =>
      `calc(100vh - ${
        props.theme?.HeaderHeight?.xs + (props.isInModal ? 50 : 0)
      }px)`};
  }
`

const MainContentWrapper = React.forwardRef((props, ref) => {
  const [dynamicHeaderHeight, setDynamicHeaderHeight] = useState(0)
  const observerRef = useRef(null)
  const isSetupRef = useRef(false)

  const heightCallback = useCallback((newHeight) => {
    setDynamicHeaderHeight(newHeight)
  }, [])

  useEffect(() => {
    if (props.mainHeaderHeight) {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
        isSetupRef.current = false
      }
      return
    }

    if (isSetupRef.current) {
      return
    }

    isSetupRef.current = true

    const getHeaderHeight = (headerId = 'mainHeader') => {
      const headerElement = document.getElementById(headerId)
      return headerElement ? headerElement.offsetHeight : 0
    }

    const createHeaderHeightObserver = (callback, headerId = 'mainHeader') => {
      const headerElement = document.getElementById(headerId)

      if (!headerElement) {
        return null
      }

      let lastHeight = getHeaderHeight(headerId)

      if (lastHeight > 0) {
        callback(lastHeight)
      }

      const checkHeightChange = () => {
        const currentHeight = getHeaderHeight(headerId)
        if (currentHeight !== lastHeight) {
          lastHeight = currentHeight
          callback(currentHeight)
        }
      }

      const observers = []

      if (window.ResizeObserver) {
        const resizeObserver = new ResizeObserver(() => {
          checkHeightChange()
        })
        resizeObserver.observe(headerElement)
        observers.push(resizeObserver)
      }

      const mutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          checkHeightChange()
        })
      })

      mutationObserver.observe(headerElement, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
      observers.push(mutationObserver)

      setTimeout(() => {
        checkHeightChange()
      }, 100)

      return {
        disconnect: () => {
          observers.forEach((observer) => observer.disconnect())
        },
      }
    }

    const observer = createHeaderHeightObserver(heightCallback)
    observerRef.current = observer

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
        isSetupRef.current = false
      }
    }
  }, [props.mainHeaderHeight, props.id])

  const effectiveHeaderHeight = props.mainHeaderHeight || dynamicHeaderHeight

  return (
    <StyledMainContentWrapper
      {...props}
      id="main"
      mainHeaderHeight={effectiveHeaderHeight}
      ref={ref}
    />
  )
})

MainContentWrapper.displayName = 'MainContentWrapper'

export default MainContentWrapper
